import { useState, useMemo, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { FaImage, FaPaperPlane } from 'react-icons/fa'
import { stcService } from '../services/stcService'
import { pcrsService } from '../services/pcrsService'
import { patientService } from '../services/patientService'
import { userService } from '../services/userService'
import type { StcBatchRun, StcRecord, StcRecordStatus, StcRecordsSortBy, StcRecordsSortDirection } from '../types/stc'
import type { User } from '../types/user'
import { formatDate } from '../utils/locale'
import { formatUserName, formatDoctorNameForClaim } from '../utils/name'
import { Modal } from '../components/Modal'
import { StcSigning } from './StcSigning'
import './StcReport.css'

const STATUS_OPTIONS: { value: '' | StcRecordStatus; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'signed', label: 'Signed' },
  { value: 'queued', label: 'Queued' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'processed', label: 'Processed' },
  { value: 'failed', label: 'Failed' },
  { value: 'rejected', label: 'Rejected' },
]

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

const SORTABLE_COLUMNS: { key: StcRecordsSortBy; label: string }[] = [
  { key: 'patientName', label: 'Patient' },
  { key: 'createdAt', label: 'Consultation date' },
  { key: 'signedAt', label: 'Date signed' },
  { key: 'processedAt', label: 'Date processed' },
]

const CLAIM_STATUS_FOR_REIMBURSEMENT: ReadonlySet<StcRecordStatus> = new Set(['submitted', 'processed'])
const CLAIM_STATUS_FOR_ESTIMATE: ReadonlySet<StcRecordStatus> = new Set(['signed', 'queued', 'submitted', 'processed'])
const ESTIMATED_CLAIM_TOTAL_TOOLTIP =
  'Estimated claim total includes signed, queued, submitted, and processed claims.'
const ESTIMATED_REIMBURSABLE_TOOLTIP =
  'Estimated reimbursable total includes submitted and processed claims only.'

const SPECIAL_SERVICE_FEES: Record<string, number> = {
  A: 24.8,
  AB: 70.0,
  AC: 50.0,
  AD: 60.0,
  AL: 100.0,
  AM: 100.0,
  B: 50.0,
  C: 24.8,
  CF: 55.0,
  CG: 100.0,
  CH: 160.0,
  CI: 110.0,
  CJ: 50.0,
  CK: 55.0,
  CL: 55.0,
  CM: 160.0,
  CN: 50.0,
  CO: 100.0,
  CQ: 110.0,
  D: 24.8,
  F: 24.8,
  G: 24.8,
  H: 24.8,
  J: 24.8,
  K: 37.21,
  L: 60.0,
  M: 62.02,
  X: 24.8,
  Y: 37.21,
  Z: 24.8,
}

function normalizeFeeCode(value?: string | null): string {
  return (value || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase().trim()
}

function getFeeInfo(record: StcRecord): { code: string | null; amount: number | null } {
  if (record.feeCode && SPECIAL_SERVICE_FEES[record.feeCode]) {
    return { code: record.feeCode, amount: SPECIAL_SERVICE_FEES[record.feeCode] }
  }
  if (typeof record.estimatedFee === 'number' && record.estimatedFee > 0) {
    return { code: record.feeCode || null, amount: record.estimatedFee }
  }

  const raw = String(record.formData?.specialService || record.formData?.micsService || '').trim()
  if (!raw || raw === '0') return { code: null, amount: null }

  const direct = normalizeFeeCode(raw)
  if (SPECIAL_SERVICE_FEES[direct]) {
    return { code: direct, amount: SPECIAL_SERVICE_FEES[direct] }
  }

  // Some historical records may store the full label ("K - Nebuliser...")
  const token = raw.split(/[\s\-/:]+/).find(Boolean)?.toUpperCase() || ''
  if (SPECIAL_SERVICE_FEES[token]) {
    return { code: token, amount: SPECIAL_SERVICE_FEES[token] }
  }

  return { code: null, amount: null }
}

function formatEuro(value: number): string {
  return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' }).format(value)
}

function toLocalDateInput(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function startOfWeekMonday(date: Date): Date {
  const d = new Date(date)
  const day = d.getDay()
  const diff = day === 0 ? -6 : 1 - day
  d.setDate(d.getDate() + diff)
  d.setHours(0, 0, 0, 0)
  return d
}

function formatPeriodDate(value: string): string {
  const raw = String(value || '').trim()
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!iso) return raw
  return `${iso[3]}-${iso[2]}-${iso[1]}`
}

type QuickDateKey =
  | 'today'
  | 'yesterday'
  | 'this-week'
  | 'last-week'
  | 'this-month'
  | 'last-month'
  | 'this-year'
  | 'last-year'
  | 'clear'

const QUICK_DATE_OPTIONS: Array<{ key: QuickDateKey; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'this-week', label: 'This week' },
  { key: 'last-week', label: 'Last week' },
  { key: 'this-month', label: 'This month' },
  { key: 'last-month', label: 'Last month' },
  { key: 'this-year', label: 'This year' },
  { key: 'last-year', label: 'Last year' },
  { key: 'clear', label: 'Clear' },
]

export function StcReport() {
  const [statusFilter, setStatusFilter] = useState<'' | StcRecordStatus>('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [sortBy, setSortBy] = useState<StcRecordsSortBy>('createdAt')
  const [sortDirection, setSortDirection] = useState<StcRecordsSortDirection>('desc')
  const [selectedRecord, setSelectedRecord] = useState<StcRecord | null>(null)
  const [formPngLoadingId, setFormPngLoadingId] = useState<string | null>(null)
  const [submitPendingRecord, setSubmitPendingRecord] = useState<StcRecord | null>(null)
  const [submitModalRecord, setSubmitModalRecord] = useState<StcRecord | null>(null)
  const [submitSelectedProviderId, setSubmitSelectedProviderId] = useState<string>('')
  const [batchModalOpen, setBatchModalOpen] = useState(false)
  const [batchSelectedProviderId, setBatchSelectedProviderId] = useState<string>('')
  const [activeBatchRunId, setActiveBatchRunId] = useState<string | null>(null)
  const [activeQuickDate, setActiveQuickDate] = useState<QuickDateKey | null>(null)
  const resolveSubmittedRef = useRef(false)
  const queryClient = useQueryClient()

  const applyQuickDate = (key: QuickDateKey) => {
    const now = new Date()
    now.setHours(0, 0, 0, 0)

    let start: string | null = null
    let end: string | null = null

    switch (key) {
      case 'today': {
        start = toLocalDateInput(now)
        end = start
        break
      }
      case 'yesterday': {
        const d = new Date(now)
        d.setDate(d.getDate() - 1)
        start = toLocalDateInput(d)
        end = start
        break
      }
      case 'this-week': {
        start = toLocalDateInput(startOfWeekMonday(now))
        end = toLocalDateInput(now)
        break
      }
      case 'last-week': {
        const thisWeekStart = startOfWeekMonday(now)
        const lastWeekStart = new Date(thisWeekStart)
        lastWeekStart.setDate(lastWeekStart.getDate() - 7)
        const lastWeekEnd = new Date(thisWeekStart)
        lastWeekEnd.setDate(lastWeekEnd.getDate() - 1)
        start = toLocalDateInput(lastWeekStart)
        end = toLocalDateInput(lastWeekEnd)
        break
      }
      case 'this-month': {
        const first = new Date(now.getFullYear(), now.getMonth(), 1)
        start = toLocalDateInput(first)
        end = toLocalDateInput(now)
        break
      }
      case 'last-month': {
        const first = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        const last = new Date(now.getFullYear(), now.getMonth(), 0)
        start = toLocalDateInput(first)
        end = toLocalDateInput(last)
        break
      }
      case 'this-year': {
        const first = new Date(now.getFullYear(), 0, 1)
        start = toLocalDateInput(first)
        end = toLocalDateInput(now)
        break
      }
      case 'last-year': {
        const first = new Date(now.getFullYear() - 1, 0, 1)
        const last = new Date(now.getFullYear() - 1, 11, 31)
        start = toLocalDateInput(first)
        end = toLocalDateInput(last)
        break
      }
      case 'clear':
      default:
        break
    }

    setStartDate(start || '')
    setEndDate(end || '')
    setActiveQuickDate(key === 'clear' ? null : key)
    setPage(1)
  }

  const { data: clinicalStaff } = useQuery({
    queryKey: ['clinical-staff'],
    queryFn: () => userService.getClinicalStaff(),
  })

  const { data: submitPendingPatient, isFetched: submitPendingPatientFetched } = useQuery({
    queryKey: ['patient', submitPendingRecord?.patientId],
    queryFn: () => patientService.getById(submitPendingRecord?.patientId || ''),
    enabled: !!submitPendingRecord?.patientId,
  })

  const openFormPng = async (recordId: string) => {
    setFormPngLoadingId(recordId)
    try {
      const url = await stcService.getFormPngUrl(recordId)
      window.open(url, '_blank', 'noopener,noreferrer')
    } finally {
      setFormPngLoadingId(null)
    }
  }

  const { data: paginatedData, isLoading, error } = useQuery({
    queryKey: ['stc-records', statusFilter || undefined, startDate || undefined, endDate || undefined, page, pageSize, sortBy, sortDirection],
    queryFn: () =>
      stcService.getRecords({
        ...(statusFilter && { status: statusFilter }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        page,
        pageSize,
        sortBy,
        sortDirection,
      }),
  })

  const { data: totalsRecordsData } = useQuery({
    queryKey: ['stc-records-fee-totals', statusFilter || undefined, startDate || undefined, endDate || undefined],
    queryFn: async () => {
      const pageSizeForTotals = 200
      const first = await stcService.getRecords({
        ...(statusFilter && { status: statusFilter }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        page: 1,
        pageSize: pageSizeForTotals,
        sortBy: 'createdAt',
        sortDirection: 'desc',
      })

      let allItems = [...(first.items || [])]
      if ((first.totalPages || 1) > 1) {
        const pages = Array.from({ length: first.totalPages - 1 }, (_, i) => i + 2)
        const rest = await Promise.all(
          pages.map((p) =>
            stcService.getRecords({
              ...(statusFilter && { status: statusFilter }),
              ...(startDate && { startDate }),
              ...(endDate && { endDate }),
              page: p,
              pageSize: pageSizeForTotals,
              sortBy: 'createdAt',
              sortDirection: 'desc',
            })
          )
        )
        allItems = allItems.concat(...rest.map((r) => r.items || []))
      }
      return allItems
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })

  const handleSort = (column: StcRecordsSortBy) => {
    if (sortBy === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(column)
      setSortDirection('asc')
    }
    setPage(1)
  }

  const items = paginatedData?.items ?? []
  const totalCount = paginatedData?.totalCount ?? 0
  const totalPages = paginatedData?.totalPages ?? 1
  const totalsItems = totalsRecordsData ?? items

  const feeSummary = useMemo(() => {
    let matchedItems = 0
    let totalEstimatedShown = 0
    let reimbursableCount = 0
    let reimbursableTotal = 0
    let estimateEligibleCount = 0
    let estimateEligibleTotal = 0
    for (const record of totalsItems) {
      const { amount } = getFeeInfo(record)
      if (amount == null) continue
      matchedItems += 1
      totalEstimatedShown += amount
      if (CLAIM_STATUS_FOR_ESTIMATE.has(record.status)) {
        estimateEligibleCount += 1
        estimateEligibleTotal += amount
      }
      if (CLAIM_STATUS_FOR_REIMBURSEMENT.has(record.status)) {
        reimbursableCount += 1
        reimbursableTotal += amount
      }
    }
    return {
      matchedItems,
      totalEstimatedShown,
      estimateEligibleCount,
      estimateEligibleTotal,
      reimbursableCount,
      reimbursableTotal,
    }
  }, [totalsItems])

  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages)
    }
  }, [page, totalPages])

  const pageNumbers = useMemo(() => {
    const maxButtons = 7
    const half = Math.floor(maxButtons / 2)
    let start = Math.max(1, page - half)
    let end = Math.min(totalPages, start + maxButtons - 1)
    if (end - start < maxButtons - 1) {
      start = Math.max(1, end - maxButtons + 1)
    }
    const pages: number[] = []
    for (let i = start; i <= end; i += 1) {
      pages.push(i)
    }
    return pages
  }, [page, totalPages])

  const pageSummary = useMemo(() => {
    if (totalCount === 0) return 'No results'
    const startIndex = (page - 1) * pageSize + 1
    const endIndex = Math.min(totalCount, page * pageSize)
    return `${startIndex}-${endIndex} of ${totalCount}`
  }, [page, pageSize, totalCount])

  useEffect(() => {
    setPage(1)
  }, [statusFilter, startDate, endDate])

  const submitMutation = useMutation({
    mutationFn: async ({ id, docNum, docNam }: { id: string; docNum: string; docNam: string }) => {
      await pcrsService.submitClaim({ stcRecordId: id, docNum, docNam })
      return stcService.submitClaim(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stc-records'] })
      setSubmitModalRecord(null)
      setSubmitPendingRecord(null)
      setSubmitSelectedProviderId('')
    },
  })

  const queueMutation = useMutation({
    mutationFn: async (id: string) => stcService.updateStatus(id, 'queued'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stc-records'] })
    },
  })
  const dequeueMutation = useMutation({
    mutationFn: async (id: string) => stcService.updateStatus(id, 'signed'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stc-records'] })
    },
  })

  const rejectMutation = useMutation({
    mutationFn: async (id: string) => stcService.updateStatus(id, 'rejected'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stc-records'] })
    },
  })

  const reacceptMutation = useMutation({
    mutationFn: async (id: string) => stcService.updateStatus(id, 'signed'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stc-records'] })
    },
  })

  const providersWithGms = useMemo(
    () => (clinicalStaff ?? []).filter((s): s is User & { gmsNumber: string } => !!s.gmsNumber?.trim()),
    [clinicalStaff]
  )
  const queuedRecords = useMemo(
    () => items.filter((r) => r.status === 'queued' && !r.lockBatchRunId),
    [items]
  )

  const { data: latestBatchRuns } = useQuery({
    queryKey: ['stc-batch-runs'],
    queryFn: () => stcService.listBatchRuns(5),
    refetchInterval: 5000,
  })

  const latestBatchRun = latestBatchRuns?.[0] ?? null
  const batchRunToTrack = useMemo<StcBatchRun | null>(() => {
    if (activeBatchRunId) {
      return latestBatchRuns?.find((run) => run.id === activeBatchRunId) ?? null
    }
    return latestBatchRun
  }, [activeBatchRunId, latestBatchRun, latestBatchRuns])

  const { data: activeBatchRun } = useQuery({
    queryKey: ['stc-batch-run', batchRunToTrack?.id],
    queryFn: () => stcService.getBatchRun(batchRunToTrack!.id),
    enabled: !!batchRunToTrack?.id,
    refetchInterval: (query) =>
      query.state.data?.status === 'running' ? 3000 : false,
  })

  const startBatchMutation = useMutation({
    mutationFn: async ({ docNum, docNam }: { docNum: string; docNam: string }) => {
      const recordIds = queuedRecords.map((r) => r.id)
      return stcService.startBatchRun({
        recordIds,
        submitDocNum: docNum,
        submitDocNam: docNam,
      })
    },
    onSuccess: (result) => {
      setActiveBatchRunId(result.batchRunId)
      setBatchModalOpen(false)
      setBatchSelectedProviderId('')
      queryClient.invalidateQueries({ queryKey: ['stc-records'] })
      queryClient.invalidateQueries({ queryKey: ['stc-batch-runs'] })
    },
  })

  useEffect(() => {
    const canResolvePatient = !submitPendingRecord?.patientId || submitPendingPatientFetched
    if (!submitPendingRecord || !clinicalStaff || !canResolvePatient || resolveSubmittedRef.current) return

    resolveSubmittedRef.current = true
    const record = submitPendingRecord
    const patient = submitPendingPatient
    const registeredGpId = patient?.practiceRegistration?.registeredGpId
    const assigned = registeredGpId
      ? clinicalStaff.find((s) => s.id === registeredGpId)
      : null

    if (assigned?.gmsNumber?.trim()) {
      submitMutation.mutate({
        id: record.id,
        docNum: assigned.gmsNumber.trim(),
        docNam: formatDoctorNameForClaim(assigned),
      })
      setSubmitPendingRecord(null)
    } else {
      setSubmitModalRecord(record)
      setSubmitPendingRecord(null)
    }
  }, [submitPendingRecord, submitPendingPatient, submitPendingPatientFetched, clinicalStaff])

  useEffect(() => {
    if (!submitPendingRecord) resolveSubmittedRef.current = false
  }, [submitPendingRecord])

  const exportCsv = () => {
    const headers = [
      'Patient',
      'GMS',
      'PPS',
      'Consultation date',
      'Form type',
      'Status',
      'Claim number',
      'Fee code',
      'Estimated fee (EUR)',
      'Date signed',
      'Date processed',
    ]
    const rows = items.map((r) => [
      ...(function () {
        const fee = getFeeInfo(r)
        return [
      r.patientName || r.patientId,
      r.gmsNumber ?? '',
      r.ppsNumber ?? '',
      r.createdAt ? formatDate(r.createdAt) : '',
      r.formType,
      r.status,
      r.claimNumber ?? '',
      fee.code ?? '',
      fee.amount != null ? fee.amount.toFixed(2) : '',
      r.signedAt ? formatDate(r.signedAt) : '',
      r.processedAt ? formatDate(r.processedAt) : '',
        ]
      })(),
    ])
    const footerRows = [
      ['Estimated claim total (signed/queued/submitted/processed)', '', '', '', '', '', '', '', feeSummary.estimateEligibleTotal.toFixed(2), '', ''],
      ['Estimated reimbursable total (submitted/processed)', '', '', '', '', '', '', '', feeSummary.reimbursableTotal.toFixed(2), '', ''],
      ['Estimated total shown (all matched fee rows)', '', '', '', '', '', '', '', feeSummary.totalEstimatedShown.toFixed(2), '', ''],
    ]
    const csv = [
      headers.join(','),
      ...rows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')),
      ...footerRows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')),
    ].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `stc-status-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const queueSignedVisible = async () => {
    const signedRecords = items.filter((r) => r.status === 'signed' && !r.lockBatchRunId)
    if (signedRecords.length === 0) return
    await Promise.all(signedRecords.map((r) => queueMutation.mutateAsync(r.id)))
  }

  const dequeueQueuedVisible = async () => {
    if (queuedRecords.length === 0) return
    await Promise.all(queuedRecords.map((r) => dequeueMutation.mutateAsync(r.id)))
  }

  const startQueuedBatch = () => {
    if (providersWithGms.length === 1) {
      setBatchSelectedProviderId(providersWithGms[0].id)
    }
    setBatchModalOpen(true)
  }

  return (
    <div className="stc-report-page" role="main">
      <header className="stc-report-header">
        <h1>STCS/SS Claim Status Report</h1>
        <p className="stc-report-subtitle">View outstanding and processed STC/MICS declarations</p>
      </header>

      <div className="stc-report-filters">
        <div className="filter-group">
          <label htmlFor="stc-filter-status">Status</label>
          <select
            id="stc-filter-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter((e.target.value || '') as '' | StcRecordStatus)}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="stc-filter-start">From date</label>
          <input
            id="stc-filter-start"
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value)
              setActiveQuickDate(null)
            }}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="stc-filter-end">To date</label>
          <input
            id="stc-filter-end"
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value)
              setActiveQuickDate(null)
            }}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="stc-filter-page-size">Per page</label>
          <select
            id="stc-filter-page-size"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value))
              setPage(1)
            }}
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <div className="stc-report-bulk-actions" role="group" aria-label="Report actions">
          <button type="button" className="btn-secondary stc-report-bulk-btn" onClick={() => window.print()}>
            Print
          </button>
          <button type="button" className="btn-primary stc-report-bulk-btn" onClick={exportCsv} disabled={items.length === 0}>
            Export CSV
          </button>
          <button
            type="button"
            className="btn-secondary stc-report-bulk-btn"
            onClick={queueSignedVisible}
            disabled={queueMutation.isPending || !items.some((r) => r.status === 'signed' && !r.lockBatchRunId)}
          >
            {queueMutation.isPending ? 'Queueing...' : 'Queue Signed'}
          </button>
          <button
            type="button"
            className="btn-secondary stc-report-bulk-btn"
            onClick={dequeueQueuedVisible}
            disabled={dequeueMutation.isPending || queuedRecords.length === 0}
          >
            {dequeueMutation.isPending ? 'Dequeuing...' : `Dequeue Queued (${queuedRecords.length})`}
          </button>
          <button
            type="button"
            className="btn-primary stc-report-bulk-btn"
            onClick={startQueuedBatch}
            disabled={startBatchMutation.isPending || queuedRecords.length === 0}
          >
            {startBatchMutation.isPending ? 'Starting batch…' : `Run Queued Batch (${queuedRecords.length})`}
          </button>
        </div>
        <div className="stc-report-quick-dates" role="group" aria-label="Quick date filters">
          {QUICK_DATE_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={`stc-report-quick-date-btn ${activeQuickDate === opt.key ? 'active' : ''}`}
              onClick={() => applyQuickDate(opt.key)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <p>Loading…</p>}
      {error && <p className="stc-report-error">Failed to load records.</p>}
      {submitMutation.isError && (
        <p className="stc-report-error" role="alert">
          Failed to submit claim. Please try again.
        </p>
      )}
      {startBatchMutation.isError && (
        <p className="stc-report-error" role="alert">
          {(startBatchMutation.error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
            (startBatchMutation.error instanceof Error ? startBatchMutation.error.message : 'Failed to start batch run')}
        </p>
      )}
      {activeBatchRun && (
        <section className="stc-report-summary" aria-label="Batch run progress">
          <div className="stc-report-summary-item">
            <span>Batch status</span>
            <strong>{activeBatchRun.status}</strong>
          </div>
          <div className="stc-report-summary-item">
            <span>Progress</span>
            <strong>
              {activeBatchRun.processedItems}/{activeBatchRun.totalItems}
            </strong>
          </div>
          <div className="stc-report-summary-item">
            <span>Submitted</span>
            <strong>{activeBatchRun.submittedCount}</strong>
          </div>
          <div className="stc-report-summary-item">
            <span>Failed</span>
            <strong>{activeBatchRun.failedCount}</strong>
          </div>
          <div className="stc-report-summary-item">
            <span>Skipped</span>
            <strong>{activeBatchRun.skippedCount}</strong>
          </div>
        </section>
      )}

      {!isLoading && !error && (
        <>
          <section className="stc-report-summary" aria-label="Fee summary">
            <div className="stc-report-summary-item">
              <span>Period</span>
              <strong>
                {startDate || endDate
                  ? `${startDate ? formatPeriodDate(startDate) : 'Start'} to ${endDate ? formatPeriodDate(endDate) : 'Today'}`
                  : 'All available dates'}
              </strong>
            </div>
            <div className="stc-report-summary-item">
              <span>Items in period</span>
              <strong>{totalsItems.length}</strong>
            </div>
            <div className="stc-report-summary-item">
              <span>Fee-matched items</span>
              <strong>{feeSummary.matchedItems}</strong>
            </div>
            <div className="stc-report-summary-item">
              <span className="stc-report-label-with-help">
                Estimated claim total
                <button type="button" className="stc-report-help-icon" title={ESTIMATED_CLAIM_TOTAL_TOOLTIP} aria-label={ESTIMATED_CLAIM_TOTAL_TOOLTIP}>
                  ?
                </button>
              </span>
              <strong>{formatEuro(feeSummary.estimateEligibleTotal)}</strong>
            </div>
            <div className="stc-report-summary-item">
              <span className="stc-report-label-with-help">
                Estimated reimbursable total
                <button type="button" className="stc-report-help-icon" title={ESTIMATED_REIMBURSABLE_TOOLTIP} aria-label={ESTIMATED_REIMBURSABLE_TOOLTIP}>
                  ?
                </button>
              </span>
              <strong>{formatEuro(feeSummary.reimbursableTotal)}</strong>
            </div>
          </section>

          <div className="stc-report-pagination" role="navigation" aria-label="Report pages">
            <div className="stc-report-page-summary" aria-live="polite">
              {pageSummary}
            </div>
            <button
                type="button"
                className="btn-secondary"
                onClick={() => setPage(1)}
                disabled={page === 1}
              >
                First
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </button>
              <div className="stc-report-page-numbers" aria-label="Report list pages">
                {pageNumbers[0] > 1 && (
                  <>
                    <button
                      type="button"
                      className={`stc-report-page-num ${page === 1 ? 'active' : ''}`}
                      onClick={() => setPage(1)}
                    >
                      1
                    </button>
                    {pageNumbers[0] > 2 && <span className="stc-report-page-ellipsis">…</span>}
                  </>
                )}
                {pageNumbers.map((pageNum) => (
                  <button
                    key={pageNum}
                    type="button"
                    className={`stc-report-page-num ${pageNum === page ? 'active' : ''}`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                ))}
                {pageNumbers[pageNumbers.length - 1] < totalPages && (
                  <>
                    {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
                      <span className="stc-report-page-ellipsis">…</span>
                    )}
                    <button
                      type="button"
                      className={`stc-report-page-num ${page === totalPages ? 'active' : ''}`}
                      onClick={() => setPage(totalPages)}
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
              >
                Last
              </button>
          </div>
          <div className="stc-report-table-wrap">
          <table className="stc-report-table" aria-label="STC records">
            <thead>
              <tr>
                {SORTABLE_COLUMNS.map(({ key, label }) => (
                  <th key={key}>
                    <button
                      type="button"
                      className={`stc-report-th-sort ${sortBy === key ? 'active' : ''}`}
                      onClick={() => handleSort(key)}
                      aria-sort={sortBy === key ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}
                    >
                      {label}
                      {sortBy === key && (
                        <span className="stc-report-sort-icon" aria-hidden>
                          {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                        </span>
                      )}
                    </button>
                  </th>
                ))}
                <th>GMS</th>
                <th>PPS</th>
                <th>Form type</th>
                <th>Status</th>
                <th>Claim #</th>
                <th>Fee code</th>
                <th>Estimated fee</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={12}>No records match the filters.</td>
                </tr>
              ) : (
                items.map((r) => {
                  const fee = getFeeInfo(r)
                  return (
                  <tr
                    key={r.id}
                    className="stc-report-row-clickable"
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedRecord(r)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setSelectedRecord(r)
                      }
                    }}
                  >
                    <td>{r.patientName || r.patientId}</td>
                    <td>{r.createdAt ? formatDate(r.createdAt) : '—'}</td>
                    <td>{r.signedAt ? formatDate(r.signedAt) : '—'}</td>
                    <td>{r.processedAt ? formatDate(r.processedAt) : '—'}</td>
                    <td>{r.gmsNumber ?? '—'}</td>
                    <td>{r.ppsNumber ?? '—'}</td>
                    <td>{r.formType}</td>
                    <td>{r.status}</td>
                    <td>{r.claimNumber ?? '—'}</td>
                    <td>{fee.code ?? '—'}</td>
                    <td className="stc-report-fee">{fee.amount != null ? formatEuro(fee.amount) : '—'}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="stc-report-actions">
                        {r.formPngS3Key && (
                          <button
                            type="button"
                            className="stc-report-action-icon"
                            onClick={() => openFormPng(r.id)}
                            disabled={formPngLoadingId === r.id}
                            aria-label={formPngLoadingId === r.id ? 'Opening…' : `View saved form for ${r.patientName || r.patientId}`}
                            title="View saved form"
                          >
                            <FaImage aria-hidden />
                          </button>
                        )}
                        {(r.status === 'signed' || r.status === 'queued') && (
                          <button
                            type="button"
                            className="stc-report-action-icon"
                            onClick={() => setSubmitPendingRecord(r)}
                            disabled={submitMutation.isPending || !!r.lockBatchRunId || (!!submitPendingRecord && submitPendingRecord.id === r.id)}
                            aria-label={
                              submitMutation.isPending && submitMutation.variables?.id === r.id
                                ? 'Submitting…'
                                : submitPendingRecord?.id === r.id
                                  ? 'Checking…'
                                  : `Submit claim for ${r.patientName || r.patientId || 'patient'}`
                            }
                            title="Submit claim"
                          >
                            <FaPaperPlane aria-hidden />
                          </button>
                        )}
                        {r.status === 'signed' && (
                          <button
                            type="button"
                            className="stc-report-action-icon"
                            onClick={() => queueMutation.mutate(r.id)}
                            disabled={queueMutation.isPending || !!r.lockBatchRunId}
                            aria-label={`Queue claim for ${r.patientName || r.patientId || 'patient'}`}
                            title="Queue claim"
                          >
                            Q
                          </button>
                        )}
                        {r.status === 'queued' && (
                          <button
                            type="button"
                            className="stc-report-action-icon"
                            onClick={() => dequeueMutation.mutate(r.id)}
                            disabled={dequeueMutation.isPending || !!r.lockBatchRunId}
                            aria-label={`Dequeue claim for ${r.patientName || r.patientId || 'patient'}`}
                            title="Dequeue claim"
                          >
                            D
                          </button>
                        )}
                        {r.status === 'signed' && (
                          <button
                            type="button"
                            className="stc-report-action-icon"
                            onClick={() => rejectMutation.mutate(r.id)}
                            disabled={rejectMutation.isPending || !!r.lockBatchRunId}
                            aria-label={`Reject claim for ${r.patientName || r.patientId || 'patient'}`}
                            title="Reject claim"
                          >
                            R
                          </button>
                        )}
                        {r.status === 'rejected' && (
                          <button
                            type="button"
                            className="stc-report-action-icon"
                            onClick={() => reacceptMutation.mutate(r.id)}
                            disabled={reacceptMutation.isPending || !!r.lockBatchRunId}
                            aria-label={`Re-accept claim for ${r.patientName || r.patientId || 'patient'}`}
                            title="Re-accept claim"
                          >
                            A
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
        </>
      )}

      {submitModalRecord && (
        <Modal
          isOpen={!!submitModalRecord}
          onClose={() => {
            if (!submitMutation.isPending) {
              setSubmitModalRecord(null)
              setSubmitSelectedProviderId('')
            }
          }}
          title="Submit claim to SSPCRS"
        >
          <p className="stc-report-submit-modal-intro">
            The patient&apos;s assigned GMS doctor does not have a GMS number (or could not be determined). Select a provider with a GMS number to submit the claim under.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              const selected = submitSelectedProviderId ? providersWithGms.find((p) => p.id === submitSelectedProviderId) : null
              if (submitModalRecord && selected?.gmsNumber?.trim()) {
                submitMutation.mutate({
                  id: submitModalRecord.id,
                  docNum: selected.gmsNumber.trim(),
                  docNam: formatDoctorNameForClaim(selected),
                })
              }
            }}
            className="stc-report-submit-form"
          >
            <div className="form-group">
              <label htmlFor="submit-provider">Provider (GMS number required)</label>
              <select
                id="submit-provider"
                value={submitSelectedProviderId}
                onChange={(e) => setSubmitSelectedProviderId(e.target.value)}
                required
                disabled={submitMutation.isPending}
                aria-describedby={providersWithGms.length === 0 ? 'submit-provider-empty-hint' : undefined}
              >
                <option value="">Select provider...</option>
                {providersWithGms.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {formatUserName(provider)} (GMS: {provider.gmsNumber})
                  </option>
                ))}
              </select>
              {providersWithGms.length === 0 && (
                <span id="submit-provider-empty-hint" className="form-hint" role="status">
                  No providers with a GMS number. Add a GMS number to a user in Settings.
                </span>
              )}
            </div>
            {submitMutation.isError && (
              <p className="form-error" role="alert">
                {(submitMutation.error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
                  (submitMutation.error instanceof Error ? submitMutation.error.message : 'Submission failed. Please try again.')}
              </p>
            )}
            <div className="stc-report-submit-form-actions">
              <button
                type="button"
                onClick={() => {
                  setSubmitModalRecord(null)
                  setSubmitSelectedProviderId('')
                }}
                disabled={submitMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitMutation.isPending || !submitSelectedProviderId || providersWithGms.length === 0}
              >
                {submitMutation.isPending ? 'Submitting…' : 'Submit to SSPCRS'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {batchModalOpen && (
        <Modal
          isOpen={batchModalOpen}
          onClose={() => {
            if (!startBatchMutation.isPending) {
              setBatchModalOpen(false)
              setBatchSelectedProviderId('')
            }
          }}
          title="Run queued claims batch"
        >
          <p className="stc-report-submit-modal-intro">
            Choose the submitting provider for this batch. All queued claims on this page will be locked while the run is active.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              const selected = providersWithGms.find((p) => p.id === batchSelectedProviderId)
              if (!selected?.gmsNumber?.trim()) return
              startBatchMutation.mutate({
                docNum: selected.gmsNumber.trim(),
                docNam: formatDoctorNameForClaim(selected),
              })
            }}
            className="stc-report-submit-form"
          >
            <div className="form-group">
              <label htmlFor="batch-provider">Submitting provider (GMS number required)</label>
              <select
                id="batch-provider"
                value={batchSelectedProviderId}
                onChange={(e) => setBatchSelectedProviderId(e.target.value)}
                required
                disabled={startBatchMutation.isPending}
              >
                <option value="">Select provider...</option>
                {providersWithGms.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {formatUserName(provider)} (GMS: {provider.gmsNumber})
                  </option>
                ))}
              </select>
            </div>
            <div className="stc-report-submit-form-actions">
              <button
                type="button"
                onClick={() => {
                  setBatchModalOpen(false)
                  setBatchSelectedProviderId('')
                }}
                disabled={startBatchMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={startBatchMutation.isPending || !batchSelectedProviderId || providersWithGms.length === 0 || queuedRecords.length === 0}
              >
                {startBatchMutation.isPending ? 'Starting…' : `Start batch (${queuedRecords.length})`}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {selectedRecord && (
        <Modal
          isOpen={!!selectedRecord}
          onClose={() => setSelectedRecord(null)}
          title={`STC / MICS – ${selectedRecord.patientName ?? selectedRecord.patientId}`}
          size="large"
        >
          <StcSigning
            embedded
            consultationIdProp={selectedRecord.consultationId}
            recordIdProp={selectedRecord.id}
            patientIdProp={selectedRecord.patientId}
            patientNameProp={selectedRecord.patientName ?? undefined}
            formTypeProp={selectedRecord.formType}
            onClose={() => setSelectedRecord(null)}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['stc-records'] })
              setSelectedRecord(null)
            }}
          />
        </Modal>
      )}
    </div>
  )
}
